import React from 'react';
import { Home, Activity, Megaphone, TrendingUp, Headphones, DollarSign, Users, Bot, GitBranch, BookOpen, Plug, PanelLeftClose, PanelLeftOpen, Target, Share2, PenTool, BarChart3, Kanban, UserCheck, FileText, LineChart, Ticket, ShieldAlert, Heart, Wallet, PieChart, ClipboardList, UserPlus, Smile, LayoutDashboard, History, Mail } from 'lucide-react';

const ALL_NAV_ITEMS = [
  { id: 'home', label: 'Home', icon: Home },
  { id: 'org-pulse', label: 'Org Pulse', icon: Activity },
  { id: 'mkt-dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { id: 'divider-dept', type: 'divider' },
  { id: 'marketing', label: 'Marketing', icon: Megaphone },
  { id: 'mkt-campaigns', label: 'Campaigns', icon: Target },
  { id: 'mkt-social', label: 'Social', icon: Share2 },
  { id: 'mkt-content', label: 'Content', icon: PenTool },
  { id: 'mkt-analytics', label: 'Analytics', icon: BarChart3 },
  { id: 'sales', label: 'Sales', icon: TrendingUp },
  { id: 'sales-deals', label: 'Deals', icon: Target },
  { id: 'sales-pipeline', label: 'Pipeline', icon: Kanban },
  { id: 'sales-outreach', label: 'Outreach', icon: Mail },
  { id: 'sales-reports', label: 'Reports', icon: LineChart },
  { id: 'support', label: 'Support', icon: Headphones },
  { id: 'support-tickets', label: 'Tickets', icon: Ticket },
  { id: 'support-churn', label: 'Churn Risk', icon: ShieldAlert },
  { id: 'support-sentiment', label: 'Sentiment', icon: Heart },
  { id: 'finance', label: 'Finance', icon: DollarSign },
  { id: 'finance-cashflow', label: 'Cashflow', icon: Wallet },
  { id: 'finance-budgets', label: 'Budgets', icon: PieChart },
  { id: 'finance-invoices', label: 'Invoices', icon: FileText },
  { id: 'finance-reports', label: 'Reports', icon: ClipboardList },
  { id: 'people', label: 'People', icon: Users },
  { id: 'people-team', label: 'Team', icon: Users },
  { id: 'people-hiring', label: 'Hiring', icon: UserPlus },
  { id: 'people-engagement', label: 'Engagement', icon: Smile },
  { id: 'people-org-health', label: 'Org Health', icon: Activity },
  { id: 'divider-system', type: 'divider' },
  { id: 'history', label: 'History', icon: History },
  { id: 'agents', label: 'Agents', icon: Bot },
  { id: 'workflows', label: 'Workflows', icon: GitBranch },
  { id: 'knowledge-vault', label: 'Knowledge Vault', icon: BookOpen },
  { id: 'integrations', label: 'Integrations', icon: Plug },
];

const NAV_BY_ROLE = {
  ceo: [
    'home', 'org-pulse', 'divider-dept',
    'marketing', 'sales', 'support', 'finance', 'people',
    'divider-system', 'history', 'agents', 'workflows', 'knowledge-vault', 'integrations'
  ],
  marketing: [
    'home', 'divider-dept',
    'mkt-campaigns', 'mkt-social', 'mkt-content', 'mkt-analytics',
    'divider-system', 'history', 'agents', 'workflows', 'knowledge-vault', 'integrations'
  ],
  sales: [
    'home', 'divider-dept',
    'sales-deals', 'sales-pipeline', 'sales-outreach', 'sales-reports',
    'divider-system', 'history', 'agents', 'workflows', 'knowledge-vault', 'integrations'
  ],
  support: [
    'home', 'divider-dept',
    'support-tickets', 'support-churn', 'support-sentiment',
    'divider-system', 'history', 'agents', 'workflows', 'knowledge-vault', 'integrations'
  ],
  people: [
    'home', 'divider-dept',
    'people-team', 'people-hiring', 'people-engagement', 'people-org-health',
    'divider-system', 'history', 'agents', 'workflows', 'knowledge-vault', 'integrations'
  ],
  finance: [
    'home', 'divider-dept',
    'finance-cashflow', 'finance-budgets', 'finance-invoices', 'finance-reports',
    'divider-system', 'history', 'agents', 'workflows', 'knowledge-vault', 'integrations'
  ],
};

const V4Sidebar = ({ isDark, activeNav, onNavChange, collapsed, onToggleCollapse, activeUser }) => {
  const textPrimary = isDark ? 'text-white/80' : 'text-slate-700';
  const textActive = isDark ? 'text-white' : 'text-slate-900';
  const hoverBg = isDark ? 'hover:bg-white/[0.06]' : 'hover:bg-black/[0.04]';
  const activeBg = isDark ? 'bg-[#0d1b2a]' : 'bg-[#f0ece4]';
  const dividerColor = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const textSecondary = isDark ? 'text-white/40' : 'text-slate-500';
  const textSub = isDark ? 'text-white/60' : 'text-slate-500';

  const allowedIds = NAV_BY_ROLE[activeUser.id] || NAV_BY_ROLE.ceo;
  const navItems = ALL_NAV_ITEMS.filter((item) => allowedIds.includes(item.id));

  return (
    <div className="flex flex-col h-full" data-testid="v4-sidebar-nav">
      {/* Collapse toggle */}
      <div className={`flex items-center ${collapsed ? 'justify-center' : 'justify-end'} px-2 pt-2 pb-1`}>
        <button
          onClick={onToggleCollapse}
          className={`p-1.5 rounded-lg transition-colors ${hoverBg}`}
          data-testid="v4-nav-collapse"
        >
          {collapsed
            ? <PanelLeftOpen className={`w-4 h-4 ${textSecondary}`} />
            : <PanelLeftClose className={`w-4 h-4 ${textSecondary}`} />}
        </button>
      </div>

      {/* Nav items */}
      <nav className="flex-1 overflow-y-auto py-1 px-2">
        {navItems.map((item) => {
          if (item.type === 'divider') {
            return <div key={item.id} className={`border-t ${dividerColor} my-2 mx-2`} />;
          }

          const isActive = activeNav === item.id;
          const Icon = item.icon;
          // Map nav items to tour groups so the onboarding spotlight can frame
          // department items vs system items (Agents/Workflows/Integrations) separately.
          const DEPT_IDS = new Set(['marketing', 'sales', 'support', 'finance', 'people',
            'mkt-campaigns', 'mkt-social', 'mkt-content', 'mkt-analytics',
            'sales-deals', 'sales-pipeline', 'sales-outreach', 'sales-reports']);
          const SYSTEM_IDS = new Set(['history', 'agents', 'workflows', 'knowledge-vault', 'integrations']);
          const tourGroup = DEPT_IDS.has(item.id) ? 'sidebar-depts'
            : SYSTEM_IDS.has(item.id) ? 'sidebar-system'
            : undefined;

          return (
            <button
              key={item.id}
              onClick={() => onNavChange(item.id)}
              className={`flex items-center ${collapsed ? 'justify-center' : 'gap-3'} w-full px-3 py-2 rounded-xl text-sm font-medium transition-colors ${isActive ? `${activeBg} ${textActive}` : `${textPrimary} ${hoverBg}`}`}
              title={collapsed ? item.label : undefined}
              data-testid={`v4-nav-${item.id}`}
              data-tour-group={tourGroup}
            >
              <Icon className="w-4 h-4 flex-shrink-0" />
              {!collapsed && <span className="truncate">{item.label}</span>}
            </button>
          );
        })}
      </nav>

      {/* Bottom section */}
      <div className={`border-t ${dividerColor} px-3 py-3`}>
        {!collapsed ? (
          <div className="flex items-center gap-2.5">
            <div className={`w-8 h-8 rounded-full bg-gradient-to-br ${activeUser.gradient} flex items-center justify-center flex-shrink-0`}>
              <span className="text-white text-[10px] font-semibold">{activeUser.initials}</span>
            </div>
            <div className="min-w-0">
              <p className={`text-sm font-medium truncate ${textPrimary}`}>{activeUser.name}</p>
              <p className={`text-[11px] ${textSecondary}`}>Pro Plan</p>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2">
            <div className={`w-7 h-7 rounded-full bg-gradient-to-br ${activeUser.gradient} flex items-center justify-center`} title={activeUser.name}>
              <span className="text-white text-[9px] font-semibold">{activeUser.initials}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default V4Sidebar;
